import { watch } from 'vue'
import router from '../router/index.js'
import { useAuth } from './auth/useAuth.js'

/**
 * Émetteur du tracking front — panneau d'activité commerciale (admin, D7/D25).
 *
 * Capture UNIQUEMENT : pages vues + durée, onglet actif/inactif, recherches
 * et filtres. Volontairement muet sur tout le reste (pas de clic nommé, pas
 * de session replay, aucun contenu de formulaire — cf. §7.8/D26).
 *
 * Actif seulement si un utilisateur back-office est connecté
 * (`useAuth().isAuthenticated`), jamais sur une route dont
 * `meta.requiresAuth === false` (login, embed, portails formateur/entreprise/
 * stagiaire, pages publiques de signature/RDV, etc. — cf. `src/router/routes.js`).
 *
 * Échec d'envoi = toujours silencieux : ni toast, ni `console.error`, ni
 * exception propagée. Le tracking ne doit jamais casser une page.
 *
 * État module (singleton volontaire, un seul émetteur par onglet, à l'image
 * de `usePostHog.js`) : `init()` est appelé une fois depuis `App.vue`,
 * `cleanup()` à sa destruction. `trackSearch`/`trackFilter` restent
 * importables depuis n'importe quel composant déjà instrumenté.
 */

const TRACK_URL = '/api/activity/track'
const FLUSH_INTERVAL_MS = 15000
const MAX_QUEUE_BEFORE_FLUSH = 20
const MAX_EVENTS_PER_REQUEST = 50 // borne serveur (§6.3), sécurité si la file déborde
const DEBOUNCE_MS = 800
const SESSION_STORAGE_KEY = 'alertis_bo_activity_session_key'
const MAX_PATH_LENGTH = 500
const MAX_SEARCH_TERM_LENGTH = 255
// Plafond de `durationMs` (BI-12) : sans borne, un onglet resté ouvert du
// vendredi soir au lundi matin produit une valeur de plusieurs dizaines
// d'heures qui, au-delà de ~24,8 jours, dépasse INTEGER côté Postgres et
// fait échouer l'INSERT serveur — erreur avalée par le catch silencieux de
// `flushQueue`, donc tout le lot de 50 événements perdu sans trace. Le
// serveur applique le même plafond (source de vérité), mais on évite déjà
// d'émettre une valeur aberrante ici. `duration_ms` reste un temps
// D'ÉCRAN plafonné, pas un temps de présence réelle.
const MAX_DURATION_MS = 4 * 60 * 60 * 1000 // 4h

// --- État module -----------------------------------------------------------
let started = false
let queue = []
let flushTimerId = null
let emitting = false // vrai entre startEmitting()/stopEmitting() (utilisateur connecté)
let stopAuthWatcher = null
let removeRouterHook = null
let visibilityHandler = null
let pagehideHandler = null
let pageshowHandler = null
let authRef = null
let sessionKeyCache = null

// Écran couramment tracké (résolu via le catalogue D27), pour fermer la page
// précédente (`page_leave`) à la navigation suivante et pour rattacher
// `focus`/`blur` à un écran.
let currentScreen = null
let pageEnteredAt = null
let focusStartedAt = null

// `resolveScreen(path)` vient d'un fichier créé en parallèle par un autre
// chantier (`src/constants/activityScreens.js`) — import dynamique et
// défensif pour ne jamais faire dépendre ce composable de son ordre de
// livraison ni casser le build si le fichier est absent au moment du build.
let resolveScreenFn = null
let resolveScreenLoadAttempted = false

const debounceTimers = new Map()

// --- Utilitaires -------------------------------------------------------

function truncate(value, maxLength) {
  if (typeof value !== 'string') return value
  return value.length > maxLength ? value.slice(0, maxLength) : value
}

function getSessionKey() {
  if (sessionKeyCache) return sessionKeyCache
  try {
    const storage = window.sessionStorage
    let key = storage.getItem(SESSION_STORAGE_KEY)
    if (!key) {
      key = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`
      storage.setItem(SESSION_STORAGE_KEY, key)
    }
    sessionKeyCache = key
    return key
  } catch {
    // sessionStorage indisponible (navigation privée stricte, etc.) : clé
    // volatile, régénérée à chaque appel plutôt que de faire planter le suivi.
    sessionKeyCache = `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`
    return sessionKeyCache
  }
}

async function loadResolveScreen() {
  if (resolveScreenLoadAttempted) return resolveScreenFn
  resolveScreenLoadAttempted = true
  try {
    const mod = await import('../constants/activityScreens.js')
    resolveScreenFn = typeof mod.resolveScreen === 'function' ? mod.resolveScreen : null
  } catch {
    resolveScreenFn = null
  }
  return resolveScreenFn
}

function safeResolveScreen(fn, path) {
  if (!fn) return null
  try {
    return fn(path)
  } catch {
    return null
  }
}

function isTracking() {
  return started && !!authRef && authRef.value === true
}

// --- File d'attente et envoi --------------------------------------------

function enqueue(event) {
  try {
    if (!isTracking()) return
    queue.push({
      ...event,
      path: event.path != null ? truncate(String(event.path), MAX_PATH_LENGTH) : null,
      occurredAt: new Date().toISOString()
    })
    if (queue.length >= MAX_QUEUE_BEFORE_FLUSH) {
      flushQueue()
    }
  } catch {
    // Le tracking ne doit jamais remonter d'exception à l'appelant.
  }
}

/**
 * Envoie le lot courant. `beacon: true` pour les envois à la fermeture
 * d'onglet / masquage (sendBeacon, avec repli fetch keepalive). Un seul
 * renvoi en cas d'échec, puis le lot est abandonné silencieusement — pas de
 * réessai indéfini, pas de log visible (cf. §7.8).
 */
async function flushQueue({ beacon = false } = {}) {
  if (queue.length === 0) return
  const batch = queue.splice(0, MAX_EVENTS_PER_REQUEST)
  const payload = JSON.stringify({ sessionKey: getSessionKey(), events: batch })

  if (beacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const sent = navigator.sendBeacon(TRACK_URL, new Blob([payload], { type: 'application/json' }))
      if (sent) return
    } catch {
      // repli sur fetch ci-dessous
    }
  }

  const attempt = () => fetch(TRACK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    keepalive: beacon,
    body: payload
  }).then((res) => {
    if (!res.ok) throw new Error(`activity track HTTP ${res.status}`)
  })

  try {
    await attempt()
  } catch {
    try {
      await attempt()
    } catch {
      // Deuxième échec (404/500/réseau) : abandon silencieux du lot.
    }
  }
}

/**
 * Purge périodique de la file (15 s). Ne POSTe jamais un lot vide : autant
 * ne pas générer de trafic pour rien depuis un onglet inactif.
 */
async function flushTick() {
  if (!isTracking()) return
  if (queue.length > 0) {
    await flushQueue()
  }
}

// --- Navigation : page_view / page_leave ---------------------------------

/** Ferme l'écran courant : `page_leave` porte le temps passé dessus, seule mesure du « temps par écran ». */
function closeCurrentScreen() {
  if (!currentScreen) return
  enqueue({
    eventType: 'page_leave',
    path: currentScreen.path,
    screenKey: currentScreen.key,
    entityType: currentScreen.entityType,
    entityId: currentScreen.entityId,
    // BI-12 : plafonné à MAX_DURATION_MS — cf. commentaire de la constante.
    durationMs: pageEnteredAt ? Math.min(Date.now() - pageEnteredAt, MAX_DURATION_MS) : null
  })
  currentScreen = null
  pageEnteredAt = null
}

async function handleRouteChange(to) {
  if (!isTracking()) return

  // Ferme la page précédente si elle était trackée.
  closeCurrentScreen()

  // Route publique (meta.requiresAuth === false) : pas de nouvelle page vue.
  if (to?.meta?.requiresAuth === false) return

  const path = to?.fullPath || to?.path || null
  if (!path) return

  const resolver = await loadResolveScreen()
  const resolved = safeResolveScreen(resolver, to.path)

  currentScreen = {
    path,
    key: resolved?.key || null,
    entityType: resolved?.entityType || null,
    entityId: resolved?.entityId ?? null
  }
  pageEnteredAt = Date.now()

  enqueue({
    eventType: 'page_view',
    path: currentScreen.path,
    screenKey: currentScreen.key,
    entityType: currentScreen.entityType,
    entityId: currentScreen.entityId
  })
}

// --- Onglet actif / inactif ------------------------------------------------

function handleFocus() {
  if (!isTracking()) return
  focusStartedAt = Date.now()
  enqueue({
    eventType: 'focus',
    path: currentScreen?.path || null,
    screenKey: currentScreen?.key || null
  })
}

function handleBlur() {
  if (!isTracking()) return
  // BI-12 : plafonné à MAX_DURATION_MS — cf. commentaire de la constante.
  const durationMs = focusStartedAt ? Math.min(Date.now() - focusStartedAt, MAX_DURATION_MS) : null
  enqueue({
    eventType: 'blur',
    path: currentScreen?.path || null,
    screenKey: currentScreen?.key || null,
    durationMs
  })
  focusStartedAt = null
  // Onglet masqué : on tente un envoi immédiat (beacon) plutôt que d'attendre
  // le prochain battement, qui pourrait ne jamais arriver si l'onglet reste caché.
  flushQueue({ beacon: true })
}

// --- Cycle de vie de l'émission --------------------------------------------

/** (Re)lance l'intervalle de purge de la file — seulement si le tracking est actif. */
function startFlushInterval() {
  if (flushTimerId || !emitting) return
  flushTimerId = setInterval(flushTick, FLUSH_INTERVAL_MS)
}

/** Arrête l'intervalle de purge, sans toucher au reste de l'état. */
function stopFlushInterval() {
  if (flushTimerId) {
    clearInterval(flushTimerId)
    flushTimerId = null
  }
}

function startEmitting() {
  if (emitting) return
  emitting = true
  focusStartedAt = Date.now()
  startFlushInterval()
  // L'utilisateur peut déjà être sur une route trackée au moment où
  // `isAuthenticated` bascule à `true` (connexion sans rechargement de page).
  handleRouteChange(router.currentRoute.value)
}

function stopEmitting() {
  emitting = false
  stopFlushInterval()
  // Meilleur effort pour ne pas perdre ce qui reste (déconnexion), sans bloquer.
  flushQueue({ beacon: true })
  queue = []
  currentScreen = null
  pageEnteredAt = null
  focusStartedAt = null
  debounceTimers.forEach((timerId) => clearTimeout(timerId))
  debounceTimers.clear()
}

function debounced(key, fn, delay) {
  if (debounceTimers.has(key)) clearTimeout(debounceTimers.get(key))
  const timerId = setTimeout(() => {
    debounceTimers.delete(key)
    fn()
  }, delay)
  debounceTimers.set(key, timerId)
}

/**
 * Composable exposant le cycle de vie du tracking front (`init`/`cleanup`,
 * à appeler une seule fois depuis `App.vue`) et les deux émetteurs
 * réutilisables depuis les listes déjà instrumentées.
 */
export function useActivityTracking() {
  function init() {
    if (started) return
    started = true

    const { isAuthenticated } = useAuth()
    authRef = isAuthenticated

    removeRouterHook = router.afterEach((to) => {
      handleRouteChange(to).catch(() => {})
    })

    visibilityHandler = () => {
      if (document.hidden) {
        // Onglet masqué -> on vide la file en attente via sendBeacon pour ne
        // rien perdre (repli déjà géré par handleBlur/flushQueue).
        handleBlur()
      } else {
        // Retour au premier plan : on ouvre une nouvelle période de focus.
        handleFocus()
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)

    pagehideHandler = () => {
      // Onglet fermé ou rechargé : sans ce `page_leave`, le temps passé sur le
      // dernier écran de la visite n'était compté nulle part.
      if (isTracking()) closeCurrentScreen()
      flushQueue({ beacon: true })
    }
    window.addEventListener('pagehide', pagehideHandler)

    pageshowHandler = (event) => {
      // Retour depuis le cache de retour arrière : le `page_leave` de `pagehide`
      // est déjà parti, l'écran est rouvert pour que le temps passé reparte.
      if (event.persisted && isTracking()) handleRouteChange(router.currentRoute.value).catch(() => {})
    }
    window.addEventListener('pageshow', pageshowHandler)

    stopAuthWatcher = watch(isAuthenticated, (loggedIn) => {
      if (loggedIn) {
        startEmitting()
      } else {
        stopEmitting()
      }
    }, { immediate: true })
  }

  function cleanup() {
    if (!started) return
    started = false

    if (removeRouterHook) {
      removeRouterHook()
      removeRouterHook = null
    }
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    if (pagehideHandler) {
      window.removeEventListener('pagehide', pagehideHandler)
      pagehideHandler = null
    }
    if (pageshowHandler) {
      window.removeEventListener('pageshow', pageshowHandler)
      pageshowHandler = null
    }
    if (stopAuthWatcher) {
      stopAuthWatcher()
      stopAuthWatcher = null
    }
    stopEmitting()
    authRef = null
  }

  /**
   * À appeler par les listes déjà instrumentées lors d'une recherche texte.
   * Debounce 800 ms (§7.8) : évite un événement par frappe.
   */
  function trackSearch(term, context) {
    if (!isTracking()) return
    if (term == null || String(term).trim() === '') return
    const key = `search:${context || 'default'}`
    const value = truncate(String(term), MAX_SEARCH_TERM_LENGTH)
    debounced(key, () => {
      enqueue({
        eventType: 'search',
        path: currentScreen?.path || null,
        screenKey: currentScreen?.key || null,
        searchTerm: value,
        props: context ? { context } : null
      })
    }, DEBOUNCE_MS)
  }

  /**
   * À appeler par les listes déjà instrumentées lors d'un changement de
   * filtres. Debounce 800 ms (§7.8). `filters` doit rester un objet léger
   * (identifiants de filtres appliqués), jamais du contenu de formulaire.
   */
  function trackFilter(filters, context) {
    if (!isTracking()) return
    if (!filters || typeof filters !== 'object') return
    const key = `filter:${context || 'default'}`
    debounced(key, () => {
      enqueue({
        eventType: 'filter',
        path: currentScreen?.path || null,
        screenKey: currentScreen?.key || null,
        props: { filters, context: context || null }
      })
    }, DEBOUNCE_MS)
  }

  return {
    init,
    cleanup,
    trackSearch,
    trackFilter
  }
}
